/**
 * SpendPlansDataTable - Spend Plans table using the reusable DataTable component
 * Pixel-perfect implementation matching Figma design specs
 */

// react
import React, { useCallback, useContext, useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// mui
import { alpha, Avatar, Box, LinearProgress, Stack, styled, Typography } from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	CalendarMonth as IconCalendar,
	Edit as IconEdit,
	Payments as IconSpendPlan,
	Visibility as IconView,
} from '@mui/icons-material';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// components
import { DataTable, ColumnDef, RowAction, FilterConfig } from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// types
import { Account, SpendPlan, WorkPlan } from '../../../../@types';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { formatDate } from '../../../../utils/dates';
import { resolveSpendPlanFxRate } from '../../../../utils/fxRate';

// Styled Components - Pixel-perfect Figma match

// Spend plan name link - blue, underlined, clickable
const SpendPlanLink = styled(Typography)({
	fontSize: 14,
	fontWeight: 600,
	lineHeight: 1.4,
	color: '#2563eb',
	cursor: 'pointer',
	textDecoration: 'underline',
	textDecorationColor: '#2563eb',
	textUnderlineOffset: 2,
	'&:hover': {
		color: '#1d4ed8',
	},
});

// Status chip with styling
const StatusChip = styled(Box)<{ status: string }>(({ status }) => {
	const getStatusStyle = () => {
		switch (status) {
			case 'APPROVED':
				return { color: '#15803d', bgcolor: '#f0fdf4', border: '#86efac' };
			case 'SUBMITTED':
				return { color: '#ca8a04', bgcolor: '#fefce8', border: '#fde047' };
			case 'COMPLETED':
				return { color: '#1d4ed8', bgcolor: '#eff6ff', border: '#93c5fd' };
			case 'DRAFT':
				return { color: '#6b7280', bgcolor: '#f9fafb', border: '#e5e7eb' };
			default:
				return { color: '#6b7280', bgcolor: '#f9fafb', border: '#e5e7eb' };
		}
	};
	const style = getStatusStyle();
	return {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: 13,
		fontWeight: 500,
		color: style.color,
		padding: '4px 10px',
		backgroundColor: style.bgcolor,
		borderRadius: 4,
		border: `1px solid ${style.border}`,
	};
});

// Types

interface SpendPlansDataTableProps {
	spendPlans: SpendPlan[];
	isLoading?: boolean;
	onEditSpendPlan: (spendPlan: SpendPlan) => void;
	onViewSpendPlan: (spendPlan: SpendPlan) => void;
	onDeleteSpendPlan: (spendPlan: SpendPlan) => void;
	onSortChange: (orderBy: string, order: 'ASC' | 'DESC') => void;
	order: 'ASC' | 'DESC';
	orderBy: string;
	page: number;
	rowsPerPage: number;
	totalCount: number;
	onPageChange: (page: number) => void;
	onRowsPerPageChange: (rowsPerPage: number) => void;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	filterValues?: Record<string, string | number>;
	onFilterChange?: (key: string, value: string | number) => void;
}

// Component

export const SpendPlansDataTable = ({
	spendPlans,
	isLoading = false,
	onEditSpendPlan,
	onViewSpendPlan,
	onDeleteSpendPlan,
	onSortChange,
	order,
	orderBy,
	page,
	rowsPerPage,
	totalCount,
	onPageChange,
	onRowsPerPageChange,
	searchValue = '',
	onSearchChange,
	filterValues = {},
	onFilterChange,
}: SpendPlansDataTableProps) => {
	// Hooks
	const navigate = useNavigate();
	const { t } = useTranslation();

	// Context
	const { project } = useContext(ContextProject);
	const { accounts, workPlans } = useContext(ContextSpendPlan);

	// Helpers

	// Resolve the full account from context so we always have balances + currency,
	// regardless of whether the spend plan's account is a bare id or a populated
	// object (the populated object from the API does not include balances).
	const getAccount = useCallback(
		(spendPlan: SpendPlan): Account | undefined => {
			if (!spendPlan.account) return undefined;
			const accountId =
				typeof spendPlan.account === 'number' ? spendPlan.account : spendPlan.account.id;
			return accounts.find((a) => a.id === accountId);
		},
		[accounts]
	);

	const getAccountName = useCallback(
		(spendPlan: SpendPlan): string => {
			if (!spendPlan.account) return '----';
			const account = getAccount(spendPlan);
			if (typeof account?.name === 'string') return account.name;
			// Fall back to a populated object's name if context has not loaded it yet
			if (typeof spendPlan.account === 'object' && typeof spendPlan.account.name === 'string') {
				return spendPlan.account.name;
			}
			const accountId =
				typeof spendPlan.account === 'number' ? spendPlan.account : spendPlan.account.id;
			return `Account #${accountId ?? 'unknown'}`;
		},
		[getAccount]
	);

	const getAccountBalance = useCallback(
		(spendPlan: SpendPlan): number => {
			const account = getAccount(spendPlan);
			return account?.balances?.[0]?.balance ?? 0;
		},
		[getAccount]
	);

	// Currency for displayed amounts pertains to the designated account, mirroring
	// the Funding Earmarks page (balance currency → account currency → fallback).
	const getAccountCurrency = useCallback(
		(spendPlan: SpendPlan): string => {
			const account = getAccount(spendPlan);
			return account?.balances?.[0]?.currency || account?.currency || spendPlan.currency || 'USD';
		},
		[getAccount]
	);

	// Resolve the spend plan's linked work plan so its planned figure can be
	// restated into the project's local currency on the work plan's budget rate.
	const getWorkPlan = useCallback(
		(spendPlan: SpendPlan): WorkPlan | undefined => {
			if (!spendPlan.workPlan) return undefined;
			const workPlanId =
				typeof spendPlan.workPlan === 'number' ? spendPlan.workPlan : spendPlan.workPlan.id;
			return workPlans.find((wp) => wp.id === workPlanId);
		},
		[workPlans]
	);

	// Local-currency equivalent of a planned amount, formatted, or null when no
	// local basis exists or the amount is already denominated in local currency.
	const getPlannedLocal = useCallback(
		(spendPlan: SpendPlan, value: number): string | null => {
			const workPlan = getWorkPlan(spendPlan);
			const fx = resolveSpendPlanFxRate(spendPlan, workPlan, project);
			if (!fx || getAccountCurrency(spendPlan) === fx.localCurrency) return null;
			return formatCurrency({ currency: fx.localCurrency, value: value * fx.rate });
		},
		[getWorkPlan, project, getAccountCurrency]
	);

	const formatTimeline = useCallback((dateStart: string | null, dateEnd: string | null): string => {
		if (!dateStart && !dateEnd) return 'No Timeline';
		if (dateStart && dateEnd) {
			return `${formatDate({ date: dateStart })} - ${formatDate({ date: dateEnd })}`;
		}
		return dateStart
			? `From ${formatDate({ date: dateStart })}`
			: dateEnd
				? `Until ${formatDate({ date: dateEnd })}`
				: '';
	}, []);

	const formatStatusLabel = useCallback((status: string): string => {
		switch (status) {
			case 'APPROVED':
				return 'Approved';
			case 'SUBMITTED':
				return 'Submitted';
			case 'COMPLETED':
				return 'Completed';
			case 'DRAFT':
				return 'Draft';
			default:
				return status;
		}
	}, []);

	// Navigation handlers
	const handleOpenSpendPlan = useCallback(
		(spendPlanId: number) => {
			navigate(`/project/${project?.idWbg}/spend-plans/spend-planning/${spendPlanId}`);
		},
		[navigate, project?.idWbg]
	);

	// Column definitions - matching old SpendPlansTableView layout
	const columns: ColumnDef<SpendPlan>[] = useMemo(
		() => [
			{
				id: 'name',
				label: t('spendPlanLabel'),
				minWidth: 200,
				sortable: true,
				render: (spendPlan) => {
					return (
						<Stack direction="row" alignItems="center" spacing={1.5}>
							<Avatar
								sx={{
									bgcolor: alpha('#7c3aed', 0.1),
									height: 36,
									width: 36,
								}}>
								<IconSpendPlan sx={{ color: '#7c3aed', fontSize: 18 }} />
							</Avatar>
							<Box sx={{ minWidth: 0, maxWidth: 180 }}>
								<TruncatedText
									text={
										typeof spendPlan.name === 'string'
											? spendPlan.name
											: `Spend Plan #${spendPlan.id}`
									}>
									<SpendPlanLink onClick={() => handleOpenSpendPlan(spendPlan.id)}>
										{typeof spendPlan.name === 'string'
											? spendPlan.name
											: `Spend Plan #${spendPlan.id}`}
									</SpendPlanLink>
								</TruncatedText>
								<Typography variant="caption" color="text.secondary">
									{formatCurrency({
										value: spendPlan.totalPlanned || 0,
										currency: getAccountCurrency(spendPlan),
									})}{' '}
									{t('spendPlan.planned')}
								</Typography>
								{getPlannedLocal(spendPlan, spendPlan.totalPlanned || 0) && (
									<Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
										{getPlannedLocal(spendPlan, spendPlan.totalPlanned || 0)}
									</Typography>
								)}
							</Box>
						</Stack>
					);
				},
			},
			{
				id: 'account',
				label: t('account'),
				minWidth: 180,
				sortable: true,
				render: (spendPlan) => {
					const accountName = getAccountName(spendPlan);
					const accountBalance = getAccountBalance(spendPlan);

					return (
						<Stack direction="row" alignItems="center" spacing={1}>
							<IconAccount sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
							<Box sx={{ minWidth: 0, maxWidth: 150 }}>
								<TruncatedText text={accountName} variant="body2" />
								<Typography variant="caption" color="text.secondary">
									Balance:{' '}
									{formatCurrency({
										value: accountBalance,
										currency: getAccountCurrency(spendPlan),
									})}
								</Typography>
							</Box>
						</Stack>
					);
				},
			},
			{
				id: 'dateStart',
				label: t('planningPeriod'),
				minWidth: 180,
				sortable: true,
				render: (spendPlan) => (
					<Stack direction="row" alignItems="center" spacing={1}>
						<IconCalendar sx={{ color: 'text.secondary', fontSize: 18 }} />
						<Typography variant="body2">
							{formatTimeline(spendPlan.dateStart || null, spendPlan.dateEnd || null)}
						</Typography>
					</Stack>
				),
			},
			{
				id: 'totalPlanned',
				label: t('planned'),
				minWidth: 120,
				align: 'right',
				sortable: false,
				render: (spendPlan) => {
					const accountBalance = getAccountBalance(spendPlan);
					const utilizationPercent =
						accountBalance > 0 ? Math.round((spendPlan.totalPlanned / accountBalance) * 100) : 0;

					return (
						<Box>
							<Typography variant="body2" fontWeight={600}>
								{formatCurrency({
									value: spendPlan.totalPlanned || 0,
									currency: getAccountCurrency(spendPlan),
								})}
							</Typography>
							{getPlannedLocal(spendPlan, spendPlan.totalPlanned || 0) && (
								<Typography variant="body2" sx={{ color: '#6b7280' }}>
									{getPlannedLocal(spendPlan, spendPlan.totalPlanned || 0)}
								</Typography>
							)}
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'flex-end',
									mt: 0.5,
								}}>
								<LinearProgress
									variant="determinate"
									value={Math.min(utilizationPercent, 100)}
									sx={{
										width: 60,
										mr: 1,
										height: 4,
										borderRadius: 2,
										bgcolor: 'grey.200',
										'& .MuiLinearProgress-bar': {
											borderRadius: 2,
											bgcolor: utilizationPercent > 80 ? '#f59e0b' : '#22c55e',
										},
									}}
								/>
								<Typography variant="caption" color="text.secondary">
									{utilizationPercent}%
								</Typography>
							</Box>
						</Box>
					);
				},
			},
			{
				id: 'status',
				label: t('status'),
				minWidth: 100,
				sortable: true,
				render: (spendPlan) => (
					<StatusChip status={spendPlan.status}>{formatStatusLabel(spendPlan.status)}</StatusChip>
				),
			},
		],
		[
			getAccountName,
			getAccountBalance,
			getAccountCurrency,
			getPlannedLocal,
			formatStatusLabel,
			formatTimeline,
			handleOpenSpendPlan,
		]
	);

	// Row actions
	const rowActions: RowAction<SpendPlan>[] = useMemo(
		() => [
			{
				label: 'View Details',
				onClick: onViewSpendPlan,
				icon: <IconView sx={{ fontSize: 18, color: '#6b7280' }} />,
			},
			{
				label: t('spendPlan.editSpendPlanLabel'),
				onClick: onEditSpendPlan,
				icon: <IconEdit sx={{ fontSize: 18, color: '#6b7280' }} />,
			},
			{
				label: 'Delete Draft',
				onClick: onDeleteSpendPlan,
				show: (sp) => sp.status === 'DRAFT',
				destructive: true,
				dividerBefore: true,
			},
		],
		[onViewSpendPlan, onEditSpendPlan, onDeleteSpendPlan]
	);

	// Filter configurations for dropdown selects
	const filterConfigs: FilterConfig[] = useMemo(
		() => [
			{
				key: 'status',
				label: t('status'),
				options: [
					{ value: 'ALL', label: 'All Statuses' },
					{ value: 'DRAFT', label: 'Draft' },
					{ value: 'SUBMITTED', label: 'Submitted' },
					{ value: 'APPROVED', label: 'Approved' },
					{ value: 'COMPLETED', label: 'Completed' },
					{ value: 'DEACTIVATED', label: 'Deactivated' },
				],
			},
			{
				key: 'account',
				label: t('account'),
				options: [
					{ value: 'ALL', label: 'All Accounts' },
					...accounts.map((account) => ({ value: account.id, label: account.name })),
				],
			},
		],
		[accounts]
	);

	return (
		<DataTable
			data={spendPlans}
			columns={columns}
			getRowKey={(spendPlan) => spendPlan.id}
			loading={isLoading}
			emptyMessage="No spend plans found"
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			page={page}
			rowsPerPage={rowsPerPage}
			totalCount={totalCount}
			onPageChange={onPageChange}
			onRowsPerPageChange={onRowsPerPageChange}
			searchPlaceholder={t('spendPlan.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
			rowActions={rowActions}
			onRowClick={(spendPlan) => handleOpenSpendPlan(spendPlan.id)}
			showFilters
			filters={filterConfigs}
			filterValues={filterValues}
			onFilterChange={onFilterChange}
		/>
	);
};

export default SpendPlansDataTable;
